/**
 * State management for the conversational onboarding flow.
 *
 * Manages profile persistence and completion. The conversational UI
 * handles its own question progression via local component state.
 */

import type { CreateUserProfileRejectionReason } from '@elara/domain/flows';
import type { LegacyPreferenceField, ProfilePreference } from '@elara/domain/models/profile-preferences';
import type { PreferenceCategory } from '@elara/domain/models/schemas/shared-schemas';
import type { AdditionalTraveller, TravelerProfile } from '@elara/domain/models/schemas/TravelerProfileSchema';

import type { UserProfileContainer } from '#containers/UserProfileContainer.js';
import type { AppFlowsContainer } from '#flows.js';
import type { KeyValueStore } from '#lib/key-value-store.js';
import type { InviteState } from './InviteState.js';
import type { SessionState } from './SessionState.js';
import type { UserProfileState } from './UserProfileState.js';

import {
  isCategorizedPreference,
  LEGACY_PREFERENCE_FIELDS,
  normalizePreferenceValue,
  withoutLegacyPreferenceFields,
} from '@elara/domain/models/profile-preferences';
import { PreferenceCategorySchema } from '@elara/domain/models/schemas/shared-schemas';

import { detectLocalePreferences } from '#lib/locale-utils.js';
import { preferencesToText, textToPreferences } from '#lib/preference-utils.js';
import { createState } from '#lib/signals-utils.js';

/** An entry's category; entries saved before categories existed count as general. */
const categoryOf = (entry: ProfilePreference): string => (isCategorizedPreference(entry) ? entry.category : 'general');

export interface FamilyData {
  travellers: AdditionalTraveller[];
}

export interface TravelPreferencesData {
  preferredAirports: string;
  domesticFlightClass: string[];
  internationalFlightClass: string[];
  hotelPreferences: string;
  wishlistDestinations: string;
  keyDatesText: string;
  travelCreditCards: string;
  preferences: string;
}

/**
 * Member-facing copy for each server-side rejection. Every reason gets a distinct, actionable
 * sentence: the old catch-all "invite code is not valid" sent members with an already-redeemed code
 * back to retry the same code indefinitely.
 */
export const PROFILE_CREATION_REJECTION_MESSAGES: Record<CreateUserProfileRejectionReason, string> = {
  invite_missing: 'Your invite code was not found. Please sign up again.',
  invite_not_found: 'We don\u2019t recognise that invite code. Please check it and sign up again.',
  invite_disabled: 'This invite code has been disabled. Please contact us for a new one.',
  invite_expired: 'This invite code has expired. Please contact us for a new one.',
  invite_already_used:
    'This invite code has already been used. If you already have an account, log in instead; otherwise contact us for a new code.',
  policy_version_stale:
    'Our Terms of Service or Privacy Policy changed while you were signing up. Please reload the page and sign up again.',
};

export function createOnboardingState(
  sessionState: SessionState,
  userProfileState: UserProfileState,
  userProfile: UserProfileContainer,
  flows: AppFlowsContainer,
  inviteState: InviteState,
  keyValueStore: KeyValueStore,
) {
  /**
   * The preference values the form has shown or saved, per person (`'member'` or a companion id),
   * normalized. A step's save removes only these when their line is deleted: anything else in the
   * profile (added meanwhile by Elara, an operator or another tab) was never in the form, so its
   * absence from the text isn't a deletion.
   */
  const formValues = new Map<string, Set<string>>();
  const MEMBER = 'member';
  /** Records what the form was filled with, once per person (the getters run on every render). */
  const rememberFormValues = (owner: string, entries: Array<{ value: string }>) => {
    if (!formValues.has(owner))
      formValues.set(owner, new Set(entries.map((entry) => normalizePreferenceValue(entry.value))));
  };

  /** Where the step a member reached is kept, per member, so a refresh resumes there. */
  const stepKey = () => (sessionState.userId ? `elara.onboarding.step.${sessionState.userId}` : undefined);

  /**
   * The in-flight profile creation, shared by every concurrent caller.
   *
   * Two surfaces ask for a profile: the signup form (so it exists before the payment screen needs a
   * Stripe customer) and the onboarding page (which keeps its create as the recovery path for a
   * signup whose create failed, and can remount while the first request is in flight). The invite
   * code behind the create is single-use, so two overlapping calls could each pass the server's
   * exists-check and race to consume it; the loser then reported the code as "already used" for a
   * profile the winner had just written. Sharing the promise makes the second caller await the
   * first rather than start a second create — a plain boolean flag would only let it skip and
   * wrongly report failure.
   */
  let createProfileInFlight: Promise<boolean> | undefined;

  const state = createState({
    isSaving: false,
    isCompleting: false,
    isCreatingProfile: false,
    profileCreated: false,
    /**
     * What the server did on the last successful `createProfile()`: `created` when this client wrote
     * the profile, `already_exists` when one was already there (a retry, a concurrent request, or a
     * profile the listener had not delivered yet). A caller that follows creation with a write only
     * the first case should get — mobile stamps `onboardingCompletedAt` on a profile it created —
     * must read this, not the boolean return, which is true for both.
     */
    profileCreationStatus: undefined as 'created' | 'already_exists' | undefined,
    profileCreationError: undefined as string | undefined,
    /**
     * Set when the server refused to create the profile (as opposed to a transport/runtime error).
     * The onboarding UI keys off this: a rejected code cannot succeed on retry, so it offers a route
     * to enter a different code instead of a "Try again" button.
     */
    profileCreationRejection: undefined as CreateUserProfileRejectionReason | undefined,
    error: undefined as Error | undefined,

    get isOnboardingComplete(): boolean {
      return !!userProfileState.profile?.onboardingCompletedAt;
    },

    /** True when the profile is loaded and ready for the onboarding UI */
    get isReady(): boolean {
      return !!userProfileState.profile || this.profileCreated;
    },

    /** True when we need to create a profile before showing onboarding */
    get needsProfileCreation(): boolean {
      return (
        sessionState.isAuthenticated && !userProfileState.isLoading && !userProfileState.profile && !this.profileCreated
      );
    },

    get shouldShowOnboarding(): boolean {
      return (
        sessionState.isAuthenticated &&
        !sessionState.isLoading &&
        !userProfileState.isLoading &&
        (!userProfileState.profile || !this.isOnboardingComplete)
      );
    },

    /** Return initial values for the family step from the profile */
    getFamilyInitial(): FamilyData {
      const profile = userProfileState.profile;
      const travellers = (profile?.travellers ?? []) as AdditionalTraveller[];
      for (const traveller of travellers) {
        if (traveller.id) rememberFormValues(traveller.id, traveller.preferences ?? []);
      }
      return { travellers: [...travellers] };
    },

    /** Return initial values for the travel preferences from the profile */
    getTravelPreferencesInitial(): TravelPreferencesData {
      const profile = userProfileState.profile;
      const tp = profile?.travelerProfile as Partial<TravelerProfile> | undefined;
      rememberFormValues(MEMBER, tp?.preferences ?? []);
      // Each topic step shows its category plus the legacy per-topic list of a profile not yet
      // migrated; the free-text step shows the categories with no step of their own (dining, general).
      const topic = (field: LegacyPreferenceField) =>
        preferencesToText([
          ...(tp?.[field] ?? []),
          ...(tp?.preferences ?? []).filter((entry) => categoryOf(entry) === LEGACY_PREFERENCE_FIELDS[field]),
        ]);
      const topicCategories = new Set<string>(Object.values(LEGACY_PREFERENCE_FIELDS));
      return {
        preferredAirports: topic('preferredAirports'),
        domesticFlightClass: tp?.domesticFlightClass || [],
        internationalFlightClass: tp?.internationalFlightClass || [],
        hotelPreferences: topic('hotelPreferences'),
        wishlistDestinations: topic('wishlistDestinations'),
        keyDatesText: topic('keyDates'),
        travelCreditCards: preferencesToText(tp?.travelCreditCards),
        preferences: preferencesToText(
          (tp?.preferences ?? []).filter((entry) => !topicCategories.has(categoryOf(entry))),
        ),
      };
    },

    /** Kick off demo trip creation in the background so it's ready when onboarding completes. */
    prepareDemoTrip() {
      flows.createDemoTrip({}).catch((err) => {
        console.error('[OnboardingState] Background demo trip creation failed', { error: err });
      });
    },

    /**
     * Create the user's profile, once. Called from the signup form on submit and from
     * OnboardingPage as the recovery path when signup's create did not land.
     *
     * Returns true on success, false on failure. A call made while another is in flight resolves
     * to that one's result instead of starting a second create.
     */
    async createProfile(): Promise<boolean> {
      // Cleared on settle rather than kept forever so a failed create stays retryable — the
      // onboarding page's "Try again" button depends on that.
      createProfileInFlight ??= this.runCreateProfile().finally(() => {
        createProfileInFlight = undefined;
      });
      return createProfileInFlight;
    },

    /** The single-flight body of {@link createProfile}; call that instead. */
    async runCreateProfile(): Promise<boolean> {
      this.isCreatingProfile = true;
      this.profileCreationError = undefined;
      this.profileCreationRejection = undefined;
      this.profileCreationStatus = undefined;

      const inviteCode = inviteState.getValidatedInviteCode();
      if (!inviteCode) {
        this.profileCreationError = PROFILE_CREATION_REJECTION_MESSAGES.invite_missing;
        this.profileCreationRejection = 'invite_missing';
        this.isCreatingProfile = false;
        return false;
      }

      const acceptance = inviteState.getPendingAcceptance();
      if (!acceptance) {
        this.profileCreationError =
          'Your acceptance of the Terms of Service and Privacy Policy was not captured. Please sign up again.';
        this.isCreatingProfile = false;
        return false;
      }

      try {
        const result = await userProfile.UserProfileClientModel.create({
          inviteCode,
          acceptedTermsVersion: acceptance.termsVersion,
          acceptedPrivacyVersion: acceptance.privacyVersion,
        });

        if (result.data.status === 'rejected') {
          const { reason } = result.data;
          console.warn('[OnboardingState] Profile creation rejected', { reason });
          this.profileCreationError = PROFILE_CREATION_REJECTION_MESSAGES[reason];
          this.profileCreationRejection = reason;
          // Drop the stored code so it is not auto-resubmitted on the next visit and cannot shadow a
          // replacement code entered on the signup page or carried by an invite link.
          inviteState.discardInviteCode();
          this.isCreatingProfile = false;
          return false;
        }

        this.profileCreationStatus = result.data.status;

        // Update locale preferences immediately after profile creation
        const localePrefs = detectLocalePreferences();
        await userProfile.UserProfileClientModel.update({
          attributes: {
            timezone: localePrefs.timezone,
            preferredCurrency: localePrefs.currency,
            locale: localePrefs.locale,
          },
        });

        this.profileCreated = true;
        return true;
      } catch (err) {
        console.error('[OnboardingState] Failed to create profile', { error: err });
        this.profileCreationError = 'Something went wrong creating your account. Please try again.';
        return false;
      } finally {
        this.isCreatingProfile = false;
      }
    },

    /**
     * Forget everything about a creation attempt, the in-flight coalescer included.
     *
     * {@link createProfile}'s single-flight is keyed on nothing but "a create is running", so it must
     * not span a change of member: the next one to ask would join the previous one's request and
     * never send their own, leaving them with no profile and nothing in flight. Mobile calls this on
     * the signed-out transition (its `ProfileProvisioningGate` lives above the session and outlives
     * it); the web flow ends at `completeOnboarding` and has no use for it.
     *
     * It does not cancel a request already sent — that one lands as it would have — it only stops a
     * later caller joining it.
     */
    resetCreationState() {
      createProfileInFlight = undefined;
      this.isCreatingProfile = false;
      this.profileCreated = false;
      this.profileCreationError = undefined;
      this.profileCreationRejection = undefined;
      this.profileCreationStatus = undefined;
    },

    /** Save profile data. Returns true on success, false on failure. */
    async saveProfile(data: Record<string, unknown>): Promise<boolean> {
      this.isSaving = true;
      this.error = undefined;

      try {
        await userProfile.UserProfileClientModel.update(data);
        return true;
      } catch (err) {
        console.error('[OnboardingState] Failed to save profile', { error: err });
        this.error = err as Error;
        return false;
      } finally {
        this.isSaving = false;
      }
    },

    /**
     * Saves one step's lines as preferences, so onboarding saves as you go and a refresh keeps the
     * answers: lines not yet stored are added in `category`, and entries of `shownCategories` that the
     * form showed (see `formValues`) and whose line was deleted are removed. Per entry (not a whole-profile save, which never removes),
     * so fixing a typo on an earlier step replaces the entry instead of adding a second one.
     */
    async savePreferenceLines({
      text,
      category,
      shownCategories = [category],
      travellerId,
    }: {
      text: string;
      category: PreferenceCategory;
      shownCategories?: PreferenceCategory[];
      travellerId?: string;
    }): Promise<boolean> {
      return this.runSave(async () => {
        const profile = userProfileState.profile;
        const owner = travellerId
          ? (profile?.travellers as AdditionalTraveller[] | undefined)?.find(
              (traveller) => traveller.id === travellerId,
            )
          : (profile?.travelerProfile as Partial<TravelerProfile> | undefined);
        const shown = (owner?.preferences ?? []).filter((entry) =>
          shownCategories.includes(categoryOf(entry) as PreferenceCategory),
        );
        const lines = textToPreferences(text).map((entry) => entry.value);
        const lineKeys = new Set(lines.map(normalizePreferenceValue));
        const shownKeys = new Set(shown.map((entry) => normalizePreferenceValue(entry.value)));
        const ownerKey = travellerId ?? MEMBER;
        const known = formValues.get(ownerKey) ?? new Set<string>();
        formValues.set(ownerKey, known);
        for (const entry of shown) {
          const key = normalizePreferenceValue(entry.value);
          if (isCategorizedPreference(entry) && known.has(key) && !lineKeys.has(key)) {
            await userProfileState.removePreference(entry);
            known.delete(key);
          }
        }
        for (const value of lines) {
          const key = normalizePreferenceValue(value);
          known.add(key);
          if (shownKeys.has(key)) continue;
          shownKeys.add(key);
          await userProfileState.addPreference({ value, category, travellerId });
        }
      });
    },

    /**
     * Saves travel settings (cabin classes, credit cards) over the stored `travelerProfile`. Preferences
     * are left out, so the server keeps the stored ones.
     */
    async saveTravelerProfileFields(changes: Partial<TravelerProfile>): Promise<boolean> {
      return this.runSave(async () => {
        const stored = (userProfileState.profile?.travelerProfile ?? {}) as Partial<TravelerProfile>;
        const { preferences: _preferences, ...rest } = withoutLegacyPreferenceFields(stored);
        const name = (userProfileState.profile?.displayName ?? '').split(' ');
        await userProfile.UserProfileClientModel.update({
          travelerProfile: { firstName: name[0] ?? '', lastName: name.slice(1).join(' '), ...rest, ...changes },
        } as never);
      });
    },

    /**
     * Saves the family step's companions one at a time by id: new ones are created (with the
     * preferences typed for them), existing ones are updated and their preference lines diffed, and
     * saved companions no longer listed are removed. Returns the list with every companion's id, so
     * returning to the step and saving again updates them rather than adding duplicates.
     */
    async saveTravellers(travellers: AdditionalTraveller[]): Promise<AdditionalTraveller[] | undefined> {
      const saved: AdditionalTraveller[] = [];
      const ok = await this.runSave(async () => {
        const stored = (userProfileState.profile?.travellers ?? []) as AdditionalTraveller[];
        for (const traveller of travellers) {
          const isStored = traveller.id !== undefined && stored.some((companion) => companion.id === traveller.id);
          const id = await userProfileState.saveTraveller(isStored ? traveller : { ...traveller, id: undefined });
          // A new companion is created with the lines typed for them; those are now in the form.
          if (!isStored)
            formValues.set(
              id,
              new Set((traveller.preferences ?? []).map((entry) => normalizePreferenceValue(entry.value))),
            );
          saved.push({ ...traveller, id });
        }
        const keptIds = new Set(saved.map((traveller) => traveller.id));
        for (const companion of stored) {
          if (companion.id && !keptIds.has(companion.id)) await userProfileState.removeTraveller(companion.id);
        }
      });
      if (!ok) return;
      // Preference lines of companions that already existed (a new one was created with its lines).
      for (const traveller of saved) {
        const existed = travellers.some((t) => t.id === traveller.id);
        if (!existed || !traveller.id) continue;
        const text = (traveller.preferences ?? []).map((entry) => entry.value).join('\n');
        const allCategories = PreferenceCategorySchema.options;
        if (
          !(await this.savePreferenceLines({
            text,
            category: 'general',
            shownCategories: allCategories,
            travellerId: traveller.id,
          }))
        ) {
          return;
        }
      }
      return saved;
    },

    /** Runs one save with the shared saving/error state. Returns true on success. */
    async runSave(save: () => Promise<void>): Promise<boolean> {
      this.isSaving = true;
      this.error = undefined;
      try {
        await save();
        return true;
      } catch (err) {
        console.error('[OnboardingState] Failed to save', { error: err });
        this.error = err as Error;
        return false;
      } finally {
        this.isSaving = false;
      }
    },

    /** The onboarding step this member last reached in this browser, to resume at after a refresh. */
    get savedStep(): number | undefined {
      const key = stepKey();
      const step = key ? Number.parseInt(keyValueStore.get(key) ?? '', 10) : Number.NaN;
      return Number.isInteger(step) && step >= 0 ? step : undefined;
    },

    /** Remembers the onboarding step this member is on (see `savedStep`). */
    rememberStep(step: number): void {
      const key = stepKey();
      if (key) keyValueStore.set(key, String(step));
    },

    async completeOnboarding(): Promise<string | undefined> {
      this.isCompleting = true;
      this.error = undefined;

      try {
        const result = await flows.completeOnboarding({});
        inviteState.clearInviteCode();
        const key = stepKey();
        if (key) keyValueStore.remove(key);
        return result.data.tripId;
      } catch (err) {
        console.error('[OnboardingState] Failed to complete onboarding', { error: err });
        this.error = err as Error;
        return undefined;
      } finally {
        this.isCompleting = false;
      }
    },
  });

  return state;
}

export type OnboardingState = ReturnType<typeof createOnboardingState>;
